import { useEffect, useMemo, useState } from "react";
import { ClipboardList, Wallet, TrendingUp, RefreshCcw, Users, ArrowUp, ArrowDown, Calendar, Loader2, AlertTriangle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { formatKr, formatInt } from "@/lib/adminMockData";
import { fetchAdminOverview, formatApiError } from "@/lib/api";
import { AdminEmployeeLauncher } from "@/components/admin/AdminEmployeeLauncher";

function KpiCard({ icon: Icon, label, value, growth, prev, prevLabel = "vs. sidste 30 dage", isCurrency = true, accent = "blue", testid }) {
    const positive = (growth || 0) >= 0;
    const accents = {
        blue: "bg-blue-50 text-blue-600",
        violet: "bg-violet-50 text-violet-600",
        emerald: "bg-emerald-50 text-emerald-600",
        cyan: "bg-cyan-50 text-cyan-600",
        amber: "bg-amber-50 text-amber-600",
    };
    return (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-5 shadow-sm" data-testid={testid}>
            <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0 ${accents[accent]}`}><Icon className="w-5 h-5" /></div>
                <div className="min-w-0 flex-1">
                    <p className="text-xs text-slate-500 font-medium">{label}</p>
                    <div className="flex items-baseline gap-2 mt-1 flex-wrap">
                        <p className="text-2xl font-bold text-slate-900">{isCurrency ? value : formatInt(value)}</p>
                        <span className={`text-xs font-semibold inline-flex items-center gap-0.5 ${positive ? "text-emerald-600" : "text-red-500"}`}>
                            {positive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                            {Math.abs((growth || 0) * 100).toFixed(1).replace(".", ",")}%
                        </span>
                    </div>
                </div>
            </div>
            <p className="text-xs text-slate-400 mt-3">{prevLabel} {isCurrency ? formatKr(prev) : formatInt(prev)}</p>
        </div>
    );
}

export default function AdminOverview() {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                setLoading(true);
                setError("");
                const d = await fetchAdminOverview();
                if (alive) setData(d);
            } catch (e) {
                if (alive) setError(formatApiError(e));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const subs = data?.subscriptions || [];
    const products = data?.product_revenue || [];
    const series = data?.timeseries || [];
    const totalSold = useMemo(() => subs.reduce((s, x) => s + (x.sold || 0), 0), [subs]);
    const totalMonthly = useMemo(() => subs.reduce((s, x) => s + (x.monthly || 0), 0), [subs]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24" data-testid="admin-overview-loading">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
        );
    }
    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex items-start gap-3" data-testid="admin-overview-error">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                    <p className="font-semibold text-red-800">Kunne ikke hente overblik</p>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
            </div>
        );
    }
    const k = data.kpis;

    return (
        <div data-testid="admin-overview">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-slate-900">Overblik</h1>
                <p className="text-sm text-slate-500 mt-1">Ejerdashboard – samlet overblik over forretningen (live data)</p>
            </div>

            {/* 5 KPI cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                <KpiCard icon={ClipboardList} label="Omkostninger / md" value={formatKr(k.costs_monthly)} growth={k.costs_growth} prev={k.costs_prev} accent="violet" testid="kpi-costs" />
                <KpiCard icon={Wallet} label="Indkomst / md" value={formatKr(k.income_monthly)} growth={k.income_growth} prev={k.income_prev} accent="blue" testid="kpi-income" />
                <KpiCard icon={TrendingUp} label="Profit / md" value={formatKr(k.profit_monthly)} growth={k.profit_growth} prev={k.profit_prev} accent="emerald" testid="kpi-profit" />
                <KpiCard icon={RefreshCcw} label="MRR" value={formatKr(k.mrr)} growth={k.mrr_growth} prev={k.mrr_prev} accent="violet" testid="kpi-mrr" />
                <KpiCard icon={Users} label="Aktive kunder" value={formatInt(k.active_customers)} growth={k.customers_growth} prev={k.customers_prev} isCurrency={false} accent="amber" testid="kpi-customers" />
            </div>

            {/* Two-column: Abonnementsoversigt + Indtjening pr. produkt */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
                <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5" data-testid="subscription-overview">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-base font-semibold text-slate-900">Abonnementsoversigt</h2>
                        <select className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white">
                            <option>Denne måned</option>
                        </select>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[11px] uppercase text-slate-400 border-b border-slate-100">
                                    <th className="py-2 text-left font-medium">Abonnement</th>
                                    <th className="py-2 text-right font-medium">Solgt</th>
                                    <th className="py-2 text-right font-medium">Andel</th>
                                    <th className="py-2 text-right font-medium">Indtjening</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {subs.map((s) => (
                                    <tr key={s.id} className="text-slate-700" data-testid={`sub-row-${s.id}`}>
                                        <td className="py-2.5 pr-2"><span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />{s.name}</span></td>
                                        <td className="py-2.5 text-right">{s.sold}</td>
                                        <td className="py-2.5 text-right">{(s.share ?? 0).toString().replace(".", ",")}%</td>
                                        <td className="py-2.5 text-right">{formatKr(s.monthly)}</td>
                                    </tr>
                                ))}
                                <tr className="font-semibold border-t border-slate-200">
                                    <td className="py-2.5">I alt</td>
                                    <td className="py-2.5 text-right">{formatInt(totalSold)}</td>
                                    <td className="py-2.5 text-right">100%</td>
                                    <td className="py-2.5 text-right">{formatKr(totalMonthly)}</td>
                                </tr>
                            </tbody>
                        </table>
                        <div className="relative h-64">
                            {totalSold > 0 ? (
                                <>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={subs.filter((s) => s.sold > 0)} dataKey="sold" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={95} paddingAngle={2}>
                                                {subs.filter((s) => s.sold > 0).map((s, i) => <Cell key={i} fill={s.color} />)}
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                        <p className="text-3xl font-bold text-slate-900">{formatInt(totalSold)}</p>
                                        <p className="text-xs text-slate-500">Samlet køb</p>
                                    </div>
                                </>
                            ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 text-sm">
                                    <p>Ingen aktive abonnementer endnu</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5" data-testid="revenue-per-product">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-base font-semibold text-slate-900">Indtjening pr. produkt</h2>
                        <select className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white">
                            <option>Denne måned</option>
                        </select>
                    </div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[11px] uppercase text-slate-400 border-b border-slate-100">
                                <th className="py-2 text-left font-medium w-8">#</th>
                                <th className="py-2 text-left font-medium">Produkt</th>
                                <th className="py-2 text-right font-medium">Indtjening / md</th>
                                <th className="py-2 text-right font-medium w-40">Andel</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {products.map((p, i) => (
                                <tr key={p.id} className="text-slate-700" data-testid={`product-row-${p.id}`}>
                                    <td className="py-2.5 text-slate-400">{i + 1}</td>
                                    <td className="py-2.5">{p.name}</td>
                                    <td className="py-2.5 text-right">{formatKr(p.monthly)}</td>
                                    <td className="py-2.5">
                                        <div className="flex items-center gap-2">
                                            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                                <div className="h-full rounded-full" style={{ width: `${p.share || 0}%`, backgroundColor: p.color }} />
                                            </div>
                                            <span className="text-xs text-slate-500 w-12 text-right">{(p.share ?? 0).toString().replace(".", ",")}%</span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            <tr className="font-semibold border-t border-slate-200">
                                <td colSpan={2} className="py-2.5">I alt</td>
                                <td className="py-2.5 text-right">{formatKr(products.reduce((s, x) => s + (x.monthly || 0), 0))}</td>
                                <td className="py-2.5 text-right">100%</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Samlet udvikling */}
            <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5" data-testid="overall-development">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <h2 className="text-base font-semibold text-slate-900">Samlet udvikling</h2>
                    <div className="flex gap-2">
                        <button className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" />Sidste 12 måneder</button>
                        <button className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white">Månedligt</button>
                    </div>
                </div>
                <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={series} margin={{ top: 16, right: 50, left: 10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Math.round(v / 1000)} k`} />
                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} iconType="circle" />
                            <Line yAxisId="left" type="monotone" dataKey="income" name="Indkomst (kr.)" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                            <Line yAxisId="left" type="monotone" dataKey="costs" name="Omkostninger (kr.)" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} />
                            <Line yAxisId="left" type="monotone" dataKey="profit" name="Profit (kr.)" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                            <Line yAxisId="right" type="monotone" dataKey="new_customers" name="Nye kunder" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 3 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Systemadgang */}
            <div className="mt-6 bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5" data-testid="admin-tools">
                <h2 className="text-base font-semibold text-slate-900" data-testid="admin-system-title">System</h2>
                <p className="text-sm text-slate-500 mt-1 mb-4" data-testid="admin-system-description">Medarbejderadgang til virksomhedens opgaver og registreringer.</p>
                <AdminEmployeeLauncher />
            </div>
        </div>
    );
}
